// PERMSEARCH1 (docs/build-briefs/PERMSEARCH1-permission-search.md). The permission editor (a role's
// permissions, and a person's "Change one thing" sheet) groups the permission catalog the way the Settings
// menu is organised, so a permission is found where the setting lives, and searches names and descriptions.
//
// Pure data and functions, no DOM, so a plain node:test file imports it. The server's catalog is the one list
// of permissions: this file only decides which Settings section each id is shown under. It never renames an
// id and never changes what a permission grants. An id this table does not know falls into "Other" instead
// of vanishing (test/permsearch1-permission-sections.test.ts fails when a catalog id is missing here).

export interface PermissionSectionDef {
  key: string;
  label: string;
  /** True for the sections that are not in the Settings menu (Clients, Reports, Other). */
  outsideSettings: boolean;
  /** The Settings rail section whose icon the heading borrows, when there is one. */
  railSection?: string;
}

/** Settings menu order, then the sections outside it. Labels are the Settings rail's own labels. */
export const PERMISSION_SECTIONS: readonly PermissionSectionDef[] = [
  { key: "money", label: "Money and pricing", outsideSettings: false, railSection: "money" },
  {
    key: "tabsPayment",
    label: "Tabs and payment",
    outsideSettings: false,
    railSection: "tabsPayment",
  },
  { key: "recipes", label: "Recipes", outsideSettings: false, railSection: "recipes" },
  { key: "inventory", label: "Inventory items", outsideSettings: false, railSection: "inventory" },
  { key: "purchasing", label: "Vendors", outsideSettings: false, railSection: "purchasing" },
  { key: "discounts", label: "Discounts", outsideSettings: false, railSection: "discounts" },
  { key: "corrections", label: "Reasons", outsideSettings: false, railSection: "corrections" },
  { key: "people", label: "Team", outsideSettings: false, railSection: "people" },
  { key: "operations", label: "Location", outsideSettings: false, railSection: "operations" },
  { key: "tablets", label: "Devices", outsideSettings: false, railSection: "tablets" },
  { key: "connections", label: "Connections", outsideSettings: false, railSection: "connections" },
  { key: "auditLog", label: "Audit log", outsideSettings: false, railSection: "auditLog" },
  { key: "network", label: "Billing", outsideSettings: false, railSection: "network" },
  { key: "clients", label: "Clients", outsideSettings: true },
  { key: "reports", label: "Reports", outsideSettings: true },
  { key: "other", label: "Other", outsideSettings: true },
];

const SECTION_BY_KEY: ReadonlyMap<string, PermissionSectionDef> = new Map(
  PERMISSION_SECTIONS.map((s) => [s.key, s]),
);

/** Each permission id that has a home in a Settings section or in Clients. Reports are matched by prefix below. */
const SECTION_OF_ID: Readonly<Record<string, string>> = {
  // Money and pricing
  "admin.location_settings.manage": "money",
  "admin.tax_rate.manage": "money",
  "money.price_override": "money",
  "money.tax_override": "money",
  "money.gratuity_override": "money",
  "report.tip_config.view": "money",
  // Tabs and payment
  "pos.tab.open": "tabsPayment",
  "pos.tab.rename": "tabsPayment",
  "pos.tab.view_all": "tabsPayment",
  "pos.tab.cancel": "tabsPayment",
  "pos.tab.merge": "tabsPayment",
  "pos.line.add": "tabsPayment",
  "pos.line.change_liquor": "tabsPayment",
  "pos.line.remove_unprepared": "tabsPayment",
  "pos.line.prepared": "tabsPayment",
  "pos.line.inventory_sale": "tabsPayment",
  "pos.ticket.id_verify": "tabsPayment",
  "pos.ticket.id_check_skip": "tabsPayment",
  "money.payment.take": "tabsPayment",
  "money.payment.preview": "tabsPayment",
  "money.saved_card.remove": "tabsPayment",
  "money.split.manage": "tabsPayment",
  "money.tab.close": "tabsPayment",
  "money.tab.reopen": "tabsPayment",
  "money.refund": "tabsPayment",
  "money.payment_link.send": "tabsPayment",
  "money.signature.view": "tabsPayment",
  "money.receipt.resend": "tabsPayment",
  "receipt.reprint": "tabsPayment",
  "receipt.tip_over_cap": "tabsPayment",
  "receipt.carry": "tabsPayment",
  "floor.terminal.select": "tabsPayment",
  "drawer.open": "tabsPayment",
  "drawer.close": "tabsPayment",
  "drawer.approve": "tabsPayment",
  "drawer.view_expected": "tabsPayment",
  "drawer.paid_in_out": "tabsPayment",
  "drawer.drop": "tabsPayment",
  // Recipes
  "admin.menu_drink.manage": "recipes",
  "admin.menu_drink.view": "recipes",
  "admin.recipe.publish": "recipes",
  // Inventory items
  "admin.liquor_product.manage": "inventory",
  "admin.liquor_product.view": "inventory",
  "inv.receipt.record": "inventory",
  "inv.receipt.view": "inventory",
  "inv.receipt.correct": "inventory",
  "inv.onhand.view": "inventory",
  "inv.count_section.manage": "inventory",
  "inv.count_session.run": "inventory",
  "inv.count_session.close": "inventory",
  "inv.count_session.reassign": "inventory",
  "inv.variance.view": "inventory",
  "inv.movement.record": "inventory",
  "inv.movement.view": "inventory",
  "inv.movement.reverse": "inventory",
  "inv.transfer.send": "inventory",
  "inv.transfer.receive": "inventory",
  "inv.transfer.view": "inventory",
  "inv.stock_flag.record": "inventory",
  "inv.stock_flag.view": "inventory",
  "inv.stock_flag.override": "inventory",
  "inv.stock_state.view": "inventory",
  "inv.cost.view": "inventory",
  // Vendors
  "inv.vendor.manage": "purchasing",
  "inv.vendor.view": "purchasing",
  "inv.vendor_offer.manage": "purchasing",
  "inv.vendor_offer.view": "purchasing",
  "inv.vendor_price_list.import": "purchasing",
  "purchasing.cart.manage": "purchasing",
  "purchasing.order.view": "purchasing",
  "purchasing.order.place": "purchasing",
  "purchasing.order.approve": "purchasing",
  "purchasing.invoice.confirm": "purchasing",
  "purchasing.approval_threshold.manage": "purchasing",
  // Discounts
  "money.discount.apply": "discounts",
  "money.discount.approve": "discounts",
  "money.scheduled_special.remove": "discounts",
  // Reasons
  "money.line.void_prepared": "corrections",
  "money.line.unprepare": "corrections",
  // Team
  "admin.user.view": "people",
  "admin.user.create": "people",
  "admin.user.deactivate": "people",
  "admin.user.reset_pin": "people",
  "admin.user.unlock": "people",
  "admin.user.permissions.manage": "people",
  // Location
  "admin.tenant_settings.view": "operations",
  "admin.location.manage": "operations",
  "admin.location_permits.view": "operations",
  "admin.location_permits.manage": "operations",
  "admin.legal_entity.view": "operations",
  "admin.legal_entity.edit": "operations",
  "admin.legal_entity.tax_id_reveal": "operations",
  "admin.legal_entity.tax_id_change": "operations",
  "admin.business_day.capture": "operations",
  "admin.manager_exception.view": "operations",
  "admin.manager_exception.resolve": "operations",
  // Devices
  "admin.device.manage": "tablets",
  "admin.terminal.manage": "tablets",
  "admin.terminal.view": "tablets",
  "printer.manage": "tablets",
  // Connections
  "admin.payments.onboard": "connections",
  "payroll.gusto.manage": "connections",
  "payroll.tips.send": "connections",
  // Audit log
  "admin.change_history.view": "auditLog",
  // Billing
  "billing.ai.manage": "network",
  "billing.invoice.view": "network",
  // Clients (outside Settings)
  "pos.guest.service_today": "clients",
  "pos.guest.attach": "clients",
  "guest.directory.duplicates.view": "clients",
  "guest.directory.search": "clients",
  "guest.directory.bulk_search": "clients",
  "guest.history.view": "clients",
  "guest.provisional.create": "clients",
  "guest.reconciliation.view": "clients",
  "guest.reconciliation.resolve": "clients",
  "admin.mytime_sync.view": "clients",
};

/** The section a permission is shown under. Every report permission lives in Reports. */
export function sectionKeyForPermission(permissionId: string): string {
  const known = SECTION_OF_ID[permissionId];
  if (known !== undefined) return known;
  if (permissionId.startsWith("report.")) return "reports";
  return "other";
}

/** True when the id has an explicit home (so a new catalog id that lands in Other is caught by the test). */
export function hasExplicitSection(permissionId: string): boolean {
  return sectionKeyForPermission(permissionId) !== "other";
}

export function sectionLabel(key: string): string {
  return SECTION_BY_KEY.get(key)?.label ?? key;
}

export function sectionDef(key: string): PermissionSectionDef {
  return SECTION_BY_KEY.get(key) ?? { key, label: key, outsideSettings: true };
}

/** A line under a permission's name where the name alone does not say what it covers. Searched like the name. */
export const PERMISSION_DESCRIPTIONS: Readonly<Record<string, string>> = {
  "admin.location_settings.manage":
    "Drink prices, shelf tiers and the money settings in this menu.",
  "admin.tax_rate.manage": "Sets the location's default sales tax rate.",
  "money.price_override": "One-time price change on a single ticket.",
  "money.tax_override": "Overrides tax on one sale, for example a tax-exempt guest.",
  "money.gratuity_override": "Edit the tip on a ticket before it closes.",
  "report.tip_config.view": "See the tip choices guests are offered.",
  "pos.tab.open": "Start a tab for a guest or a table.",
  "pos.tab.view_all": "Needed to cover another bartender's section.",
  "money.tab.reopen": "Puts a paid tab back in edit mode.",
  "money.refund": "Full or partial refund to the original card.",
  "admin.menu_drink.manage": "Includes the drink's recipe, ingredients, price and shelf tier.",
  "admin.menu_drink.view": "See each drink and its recipe.",
  "admin.recipe.publish": "Sends a finished recipe to every location you own.",
  "inv.cost.view": "Used to work out what each recipe costs to make.",
  "money.discount.apply": "Member discount, Ten Off and other saved discounts.",
  "money.line.void_prepared": "Asks for a reason from the Reasons list.",
  "admin.user.permissions.manage": "Opens the Change one thing sheet for a person.",
  "admin.change_history.view": "Who changed what, and when.",
  "report.menu_profitability.view": "Margin for each drink, using its recipe cost.",
};

export interface SectionRows<T> {
  key: string;
  label: string;
  outsideSettings: boolean;
  rows: T[];
}

/** Rows grouped by Settings section, in Settings order. A section with no rows is left out. Row order inside a
 * section is the catalog order the server sent. */
export function groupBySettingsSection<T extends { permissionId: string }>(
  rows: readonly T[],
): SectionRows<T>[] {
  const by = new Map<string, T[]>();
  for (const row of rows) {
    const key = sectionKeyForPermission(row.permissionId);
    const list = by.get(key) ?? [];
    list.push(row);
    by.set(key, list);
  }
  const out: SectionRows<T>[] = [];
  for (const def of PERMISSION_SECTIONS) {
    const list = by.get(def.key);
    if (list !== undefined && list.length > 0) {
      out.push({
        key: def.key,
        label: def.label,
        outsideSettings: def.outsideSettings,
        rows: list,
      });
    }
  }
  return out;
}

export function normalizeQuery(query: string): string {
  return query.trim().toLowerCase().replace(/\s+/g, " ");
}

export interface PermissionHit<T> {
  row: T;
  /** "Section: Recipes" when only the section name matched, otherwise null. */
  why: string | null;
}

export interface SearchedSection<T> extends SectionRows<T> {
  hits: PermissionHit<T>[];
}

/** Sections that contain a match for the query, each with only its matching rows. A row matches when the query
 * is in its name or description, or when it is in the section's name (then the row says why). An empty query
 * returns every row of every section. Case does not matter; words are matched as one phrase. */
export function searchPermissionSections<T extends { permissionId: string; label: string }>(
  rows: readonly T[],
  query: string,
): SearchedSection<T>[] {
  const q = normalizeQuery(query);
  const sections = groupBySettingsSection(rows);
  const out: SearchedSection<T>[] = [];
  for (const section of sections) {
    const hits: PermissionHit<T>[] = [];
    for (const row of section.rows) {
      if (q === "") {
        hits.push({ row, why: null });
        continue;
      }
      const description = PERMISSION_DESCRIPTIONS[row.permissionId] ?? "";
      if (row.label.toLowerCase().includes(q) || description.toLowerCase().includes(q)) {
        hits.push({ row, why: null });
      } else if (section.label.toLowerCase().includes(q)) {
        hits.push({ row, why: `Section: ${section.label}` });
      }
    }
    if (hits.length > 0) out.push({ ...section, hits });
  }
  return out;
}

/** Splits text around the first match, so a screen can wrap the match in a highlight with text nodes only. */
export function splitForHighlight(
  text: string,
  query: string,
): { before: string; match: string; after: string } | null {
  const q = normalizeQuery(query);
  if (q === "") return null;
  const at = text.toLowerCase().indexOf(q);
  if (at < 0) return null;
  return {
    before: text.slice(0, at),
    match: text.slice(at, at + q.length),
    after: text.slice(at + q.length),
  };
}

function plural(n: number, one: string, many: string): string {
  return `${n} ${n === 1 ? one : many}`;
}

/** "42 permissions in 9 sections" when browsing; "3 permissions in 2 sections match" while searching. */
export function permissionCountText(
  sections: readonly { rows?: readonly unknown[]; hits?: readonly unknown[] }[],
  searching: boolean,
): string {
  const total = sections.reduce((n, s) => n + (s.hits ?? s.rows ?? []).length, 0);
  const base = `${plural(total, "permission", "permissions")} in ${plural(sections.length, "section", "sections")}`;
  return searching ? `${base} match` : base;
}

export function noMatchText(query: string): { headline: string; hint: string } {
  return {
    headline: `No permissions match "${query.trim()}".`,
    hint: 'Try a shorter word, like "stock" or "tab".',
  };
}
